import CloseIcon from "@mui/icons-material/Close";
import { Modal, Box, Typography, IconButton } from "@mui/material";
import { useFormik } from "formik";
import React from "react";

import * as Yup from "yup";

import Button from "../components/Button";
import Input from "../components/Input";
import { showError, showToast } from "../helpers/showToast";
import { usePostChangePasswordMutation } from "../service/auth";
import { CommonBody } from "../types/General";
import { generateEncryptedKeyBody } from "../utils/crypto";

export const ChangePasswordModal = ({ open, handleClose }: any) => {
  const [changePassword] = usePostChangePasswordMutation();

  const formik = useFormik({
    initialValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      oldPassword: Yup.string().required("Old password is required."),
      newPassword: Yup.string()
        .required("New password is required.")
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\W).{8,}$/,
          "Password must be at least 8 characters long and include 1 uppercase, 1 lowercase, and 1 special character."
        )
        .notOneOf(
          [Yup.ref("oldPassword")],
          "New password must be different from old password."
        ),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword")], "Passwords must match.")
        .required("Confirm password is required."),
    }),
    onSubmit: async () => {
      formik.setSubmitting(true);
      const body = {
        oldPassword: formik.values.oldPassword,
        newPassword: formik.values.confirmPassword,
      };

      try {
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response = await changePassword(encryptedBody).unwrap();
        if (response?.statusCode === 200) {
          formik.resetForm();
          showToast("Password changed successfully");
          handleClose();
        }
      } catch (error: any) {
        showError(error?.data?.message || "Something went wrong");
      }
      formik.setSubmitting(false);
    },
  });

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          width: 400,
          bgcolor: "background.paper",
          p: 3,
          borderRadius: 2,
          boxShadow: 24,
          margin: "auto",
          mt: "10%",
          position: "relative",
        }}
      >
        <IconButton
          onClick={() => {
            handleClose();
            formik.resetForm();
          }}
          sx={{ position: "absolute", right: 5, top: 5 }}
        >
          <CloseIcon sx={{ color: "black" }} />
        </IconButton>
        <Typography variant="h6" gutterBottom>
          Change Password
        </Typography>
        <form onSubmit={formik.handleSubmit}>
          <Input
            placeholder="Old Password"
            password
            id="oldPassword"
            name="oldPassword"
            value={formik.values.oldPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            helperText={formik.touched.oldPassword && formik.errors.oldPassword}
          />
          <Input
            placeholder="New Password"
            password
            id="newPassword"
            name="newPassword"
            value={formik.values.newPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            helperText={formik.touched.newPassword && formik.errors.newPassword}
          />
          <Input
            placeholder="Confirm Password"
            password
            id="confirmPassword"
            name="confirmPassword"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            helperText={
              formik.touched.confirmPassword && formik.errors.confirmPassword
            }
          />
          <Button value="Update Password" type="submit" />
        </form>
      </Box>
    </Modal>
  );
};
